<template>
  <main class="page-operative page-operative-data-analysis page-operative-data-analysis-finanziamenti page-operative-data-analysis-finanziamenti-list d-flex flex-column h-100">
    <div class="flex-grow-1 container-fluid d-flex flex-column h-100">
      <div class="d-flex align-items-center justify-content-between flex-wrap flex-md-nowrap mb-5">
        <PageTitle class="mb-2 mb-md-0">Contributi a fondo perduto</PageTitle>

        <app-router-link v-if="userCanEdit" class="btn btn-primary" :to="{ name: 'operative-company.data-analysis.contributi-fondo-perduto.add' }">
          Aggiungi contributi a fondo perduto
        </app-router-link>
      </div>

      <div class="flex-grow-1 d-flex flex-column h-100 overflow-auto">
        <LoadingDataLoader v-if="isLoadingData"></LoadingDataLoader>
        <ErrorOnLoadingDataFeedback v-else-if="errorLoadingData"></ErrorOnLoadingDataFeedback>
        <template v-else>
          <div class="d-flex align-items-center justify-content-between">
            <ForecastListSwitcher :viewMode="viewMode" @update:viewMode="setViewMode" />

            <BFormCheckbox v-if="viewMode === 'forecast'" v-model="isSticky" class="no-select" :switch="true">
              Blocca aggregato in alto
            </BFormCheckbox>
          </div>

          <template v-if="viewMode === 'forecast' && data">
            <div class="overflow-auto report-texts-size">
              <OperativeTable dense sticky>
                <template v-for="(table, i) in aggregateTableRows">
                  <OperativeTableAggregateTable v-if="i === 0" :key="'aggregate-' + i" :sticky="isSticky">
                    <OperativeTableBodyHeader :years="aggregateTableHeadings" show-years>Aggregato</OperativeTableBodyHeader>
                    <OperativeTableBodyRow :values="table.data.concessione">
                      Concessione
                      <template #cell-content="{ value }">{{ value | money }}</template>
                    </OperativeTableBodyRow>
                    <OperativeTableBodyRow :values="table.data.componenteDiRicavo">
                      Contributi a fondo perduto
                      <template #cell-content="{ value }">{{ value | money }}</template>
                    </OperativeTableBodyRow>
                    <OperativeTableBodyRow :values="table.data.erogazione">
                      Erogazione
                      <template #cell-content="{ value }">{{ value | money }}</template>
                    </OperativeTableBodyRow>
                    <OperativeTableBodyRow :values="table.data.credito">
                      Credito
                      <template #cell-content="{ value }">{{ value | money }}</template>
                    </OperativeTableBodyRow>
                    <OperativeTableBodyRow :values="table.data.risconto">
                      Risconto
                      <template #cell-content="{ value }">{{ value | money }}</template>
                    </OperativeTableBodyRow>
                    <OperativeTableEmptyBodyRow v-if="i !== table.data.length - 1" :fixedNumColumns="aggregateTableHeadings.length + 1" />
                  </OperativeTableAggregateTable>

                  <tbody v-else :key="'item-' + i">
                    <OperativeTableBodyHeader :years="aggregateTableHeadings" :show-years="openedRows[table.id]">
                      <p class="mb-0 px-1" @click="toggleRow(table.id)" style="user-select: none; cursor: pointer">
                        <i v-if="openedRows[table.id]" class="fas fa-caret-up"></i>
                        <i v-else class="fas fa-caret-down"></i>
                        {{ table.name }}
                      </p>
                    </OperativeTableBodyHeader>
                    <template v-if="openedRows[table.id]">
                      <OperativeTableBodyRow :values="table.data.concessione">
                        Concessione
                        <template #cell-content="{ value }">{{ value | money }}</template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.contributiFondoPerduto">
                        Contributi a fondo perduto
                        <template #cell-content="{ value }">{{ value | money }}</template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.erogazione">
                        Erogazione
                        <template #cell-content="{ value }">{{ value | money }}</template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.credito">
                        Credito
                        <template #cell-content="{ value }">{{ value | money }}</template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.risconto">
                        Risconto
                        <template #cell-content="{ value }">{{ value | money }}</template>
                      </OperativeTableBodyRow>
                    </template>
                    <OperativeTableEmptyBodyRow v-if="i !== aggregateTableRows.length - 1" :fixedNumColumns="aggregateTableHeadings.length + 1" />
                  </tbody>
                </template>
              </OperativeTable>
            </div>
          </template>
          <ContributiFondoPerdutoListTable v-else-if="viewMode === 'list' && results" class="report-texts-size" :contributi-fondo-perduto="results" :edit-enabled="userIsAdmin || userIsContextCompanyConsultant || userIsContextCompanyOwner" @delete="removeContributoFondoPerduto" />
        </template>
      </div>
    </div>
  </main>
</template>

<script>

import operativePageMixin from '@/mixins/operativePage.js';
import loadingDataMixin from '@/mixins/loadingData.js';
import computedFinancialDataMixin from '@/mixins/computedFinancialData.js';
import confirmWithModalMixin from '@/libs/Confirm/mixins/confirmWithModal.js';

import formatMoneyFilter from '@/filters/formatMoney.js';

import { BFormCheckbox } from 'bootstrap-vue';

export default {
  mixins: [operativePageMixin, loadingDataMixin, computedFinancialDataMixin, confirmWithModalMixin],
  components: {
    BFormCheckbox,
    PageTitle: () => import('@/views/components/Layout/LayoutOperative/PageTitle.vue'),
    ErrorOnLoadingDataFeedback: () => import('@/views/components/Operative/ErrorOnLoadingDataFeedback.vue'),
    LoadingDataLoader: () => import('@/views/components/Operative/LoadingDataLoader.vue'),
    OperativeTable: () => import('@/views/components/Operative/OperativeTable.vue'),
    OperativeTableBodyHeader: () => import('@/views/components/Operative/OperativeTableBodyHeader.vue'),
    OperativeTableBodyRow: () => import('@/views/components/Operative/OperativeTableBodyRow.vue'),
    OperativeTableEmptyBodyRow: () => import('@/views/components/Operative/OperativeTableEmptyBodyRow.vue'),
    OperativeTableAggregateTable: () => import('@/views/components/Operative/OperativeTable/OperativeTableAggregateTable.vue'),
    ForecastListSwitcher: () => import('@/views/components/Operative/ForecastListSwitcher.vue'),
    ContributiFondoPerdutoListTable: () => import('@/views/components/Operative/ListTable/ContributiFondoPerdutoListTable.vue'),
    AppRouterLink: () => import('@/views/components/Layout/AppRouterLink.vue'),
  },
  filters: {
    money: formatMoneyFilter,
  },
  data () {
    return {
      data: null,
      results: null,
      viewMode: 'forecast',
      isSticky: true,
      openedRows: {},
    };
  },
  computed: {
    aggregateTableHeadings () {
      if (!this.data || this.data.length < 0) {
        return [];
      }

      return this.convertToMonthsHeading(this.data[0].data);
    },
    aggregateTableRows () {
      if (!this.data || this.data.length < 0) {
        return [];
      }

      return this.data.map(d => ({
        id: d.id,
        name: d.name,
        data: this.convertToRowGroup(d.data, [
          'concessione',
          'componenteDiRicavo',
          'erogazione',
          'credito',
          'risconto',
        ]),
      }));
    },
  },
  watch: {
    company: {
      immediate: true,
      handler: function () {
        this.setViewMode('forecast');
      },
    },
  },
  methods: {
    setViewMode (mode) {
      if (mode === 'forecast') {
        this.retrieveForecastData();
      } else if (mode === 'list') {
        this.retrieveListData();
      }
      this.viewMode = mode;
    },
    retrieveForecastData () {
      if (!this.company) {
        return;
      }

      this.resetErrorOnLoadingData();
      this.setDataLoading();
      this.$api.getComputedContributiFondoPerduto(this.company?.id)
        .then(res => {
          this.data = res?.data?.data || [];
        })
        .catch(this.setErrorOnLoadingData)
        .finally(this.setDataLoaded)
      ;
    },
    retrieveListData () {
      if (!this.company) {
        return;
      }

      this.resetErrorOnLoadingData();
      this.setDataLoading();
      this.$api.getContributiFondoPerduto(this.company?.id)
        .then(res => {
          this.results = res?.data?.contributiFondoPerduto || [];
        })
        .catch(this.setErrorOnLoadingData)
        .finally(this.setDataLoaded)
      ;
    },
    removeContributoFondoPerduto (id) {
      this.confirm('Cancellare il contributo a fondo perduto selezionato? L\'operazione è irreversibile', 'Ok', 'Annulla')
        .then(confirmed => {
          if (!confirmed) {
            return;
          }

          this.$api.deleteContributoFondoPerduto(this.company.id, id).then(() => this.retrieveListData()).catch(this.$log.error);
        })
      ;
    },
    toggleRow (id) {
      if (undefined === this.openedRows[id]) {
        this.$set(this.openedRows, id, true);

        return;
      }

      this.$set(this.openedRows, id, !this.openedRows[id]);
    },
  },
};

</script>
